'use client';

import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

interface PermissionContextValue {
  permissions: Set<string>;
  loading: boolean;
  roleType: string;
  roleName: string;
  userProfile: any;
  canView: (module: string) => boolean;
  canCreate: (module: string) => boolean;
  canEdit: (module: string) => boolean;
  canDelete: (module: string) => boolean;
  canApprove: (module: string) => boolean;
  canExport: (module: string) => boolean;
  canImport: (module: string) => boolean;
  canManageSettings: () => boolean;
  hasPermission: (key: string) => boolean;
  refreshPermissions: () => Promise<void>;
  isSuperAdmin: boolean;
}

const defaultValue: PermissionContextValue = {
  permissions: new Set(),
  loading: true,
  roleType: '',
  roleName: '',
  userProfile: null,
  canView: () => false,
  canCreate: () => false,
  canEdit: () => false,
  canDelete: () => false,
  canApprove: () => false,
  canExport: () => false,
  canImport: () => false,
  canManageSettings: () => false,
  hasPermission: () => false,
  refreshPermissions: async () => {},
  isSuperAdmin: false,
};

const PermissionContext = createContext<PermissionContextValue>(defaultValue);

export const usePermissions = () => useContext(PermissionContext);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [roleType, setRoleType] = useState('');
  const [roleName, setRoleName] = useState('');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setPermissions(new Set());
        setIsSuperAdmin(false);
        setLoading(false);
        return;
      }

      // Check if super admin via user metadata or email
      const isSA = user.user_metadata?.role === 'super_admin' ||
                   user.app_metadata?.role === 'super_admin';

      if (isSA) {
        setIsSuperAdmin(true);
        setRoleType('super_admin');
        setRoleName('Super Admin');
        setPermissions(new Set(['*']));
        setLoading(false);
        return;
      }

      // Fetch user roles from managed_users + user_roles
      const { data: managedUser } = await supabase
        .from('managed_users')
        .select('id, full_name, email, status, user_roles(rbac_roles(id, name, is_system))')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      if (managedUser) {
        setUserProfile(managedUser);
        const roles = (managedUser.user_roles || []).map((ur: any) => ur.rbac_roles).filter(Boolean);
        const firstRole = roles[0];

        if (firstRole) {
          setRoleName(firstRole.name);
          setRoleType(firstRole.name.toLowerCase().replace(/\s+/g, '_'));

          // Admin role gets full access
          if (firstRole.name === 'Admin' || firstRole.name === 'Super Admin') {
            setIsSuperAdmin(true);
            setPermissions(new Set(['*']));
            setLoading(false);
            return;
          }

          // Fetch role permissions from role_permissions + rbac_permissions
          const roleIds = roles.map((r: any) => r.id);
          const { data: rolePerms } = await supabase
            .from('role_permissions')
            .select('rbac_permissions(key, system_modules(key))')
            .in('role_id', roleIds);

          const permSet = new Set<string>();
          (rolePerms || []).forEach((rp: any) => {
            const perm = rp.rbac_permissions;
            if (perm) {
              const moduleKey = perm.system_modules?.key || 'general';
              permSet.add(`${moduleKey}:${perm.key}`);
              permSet.add(perm.key);
            }
          });

          setPermissions(permSet);
        }
      } else {
        // No managed_users record found — grant minimal access (not full admin)
        setIsSuperAdmin(false);
        setRoleType('viewer');
        setRoleName('Viewer');
        setPermissions(new Set());
      }
    } catch (err) {
      console.error('Permission load error:', err);
      // On error, grant no permissions to avoid security bypass
      setIsSuperAdmin(false);
      setPermissions(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
    const supabase = createClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadPermissions();
    });
    return () => subscription.unsubscribe();
  }, [loadPermissions]);

  const has = useCallback((key: string) => {
    if (isSuperAdmin || permissions.has('*')) return true;
    return permissions.has(key);
  }, [isSuperAdmin, permissions]);

  const canView = useCallback((module: string) =>
    has('*') || has(`${module}:view`) || has('view'), [has]);

  const canCreate = useCallback((module: string) =>
    has('*') || has(`${module}:create`) || has('create'), [has]);

  const canEdit = useCallback((module: string) =>
    has('*') || has(`${module}:edit`) || has('edit'), [has]);

  const canDelete = useCallback((module: string) =>
    has('*') || has(`${module}:delete`) || has('delete'), [has]);

  const canApprove = useCallback((module: string) =>
    has('*') || has(`${module}:approve`) || has('approve'), [has]);

  const canExport = useCallback((module: string) =>
    has('*') || has(`${module}:export`) || has('export'), [has]);

  const canImport = useCallback((module: string) =>
    has('*') || has(`${module}:import`) || has('import'), [has]);

  const canManageSettings = useCallback(() =>
    has('*') || has('settings:manage') || has('admin:manage'), [has]);

  const hasPermission = useCallback((key: string) => has(key), [has]);

  return (
    <PermissionContext.Provider value={{
      permissions,
      loading,
      roleType,
      roleName,
      userProfile,
      canView,
      canCreate,
      canEdit,
      canDelete,
      canApprove,
      canExport,
      canImport,
      canManageSettings,
      hasPermission,
      refreshPermissions: loadPermissions,
      isSuperAdmin,
    }}>
      {children}
    </PermissionContext.Provider>
  );
}
